'use client'
import { type FC, Suspense, useState } from 'react'
import { Flex, Heading, Text, VStack } from '@chakra-ui/react'
import { EmptyMessage, GrandSection } from 'ui'
import { ChangePasswordForm, type ChangePasswordSubmitData } from 'forms'
import { BiError } from 'react-icons/bi'
import fetchJson, { FetchError } from 'api/utils/next/fetchJson'
import { useRouter, useSearchParams } from 'next/navigation'
import type { OverridesContextType } from 'ui/lib/overrides'
import { OverridesContextProvider, overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import type { User } from '../../utils'
import { useUser } from '../../index'

interface RegisterPageStdConfig {
  paths: {
    setPasswordApiRoute: string
  }
  redirects: {
    onLoggedIn: string
  }
  overrides?: OverridesContextType
}

export interface RegisterPageStdOverrides {
  accountRegistration?: string
  registerAccount?: string
}

interface RegisterPageStdProps {
  config: RegisterPageStdConfig
}

const RegisterPageStd: FC<RegisterPageStdProps> = ({ config }) => {
  const [errorMsg, setErrorMsg] = useState('')
  const overrides = useOverridesContext<keyof RegisterPageStdOverrides>()
  const searchParams = useSearchParams()
  const router = useRouter()
  const { mutateUser } = useUser()

  const title = overrideText('Skapa lösenord', overrides?.registerAccount)
  const subtitle = overrideText(
    'För att lättare följa beställningar kan du skapa ett lösenord för kontot.',
    overrides?.accountRegistration
  )

  if (!searchParams?.get('token')) {
    return <EmptyMessage icon={<BiError size={80} />} text="Missing QS parameters" />
  }

  const handleAccountRegister = async (data: ChangePasswordSubmitData) => {
    try {
      const user: User = await fetchJson(config.paths.setPasswordApiRoute, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activationToken: searchParams?.get('token'),
          newPassword: data.password
        })
      })
      await mutateUser(user, false)
      setErrorMsg('')
      // Invalidate cached RSC payloads so server-side gates see the new session.
      router.refresh()
      router.push(config.redirects.onLoggedIn)
    } catch (error) {
      if (error instanceof FetchError) {
        setErrorMsg(error.data.message)
      } else {
        console.error('An unexpected error happened:', error)
      }
    }
  }

  return (
    <OverridesContextProvider overrides={{ ...config.overrides }}>
      <GrandSection sectionId="registrera">
        <Flex justify="center" w="full">
          <VStack
            align="stretch"
            bg="background.default"
            borderColor="gray.100"
            borderRadius="md"
            borderWidth={1}
            maxW="md"
            p={{ base: 6, md: 8 }}
            spacing={6}
            w="full"
          >
            <VStack align="stretch" spacing={2}>
              <Heading as="h1" fontSize={{ base: '2xl', md: '3xl' }}>
                {title}
              </Heading>
              <Text color="text.mild" fontSize="sm">
                {subtitle}
              </Text>
            </VStack>

            <ChangePasswordForm error={errorMsg} onSubmit={handleAccountRegister} />
          </VStack>
        </Flex>
      </GrandSection>
    </OverridesContextProvider>
  )
}

// https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout
const RegisterPageStdSuspended: FC<RegisterPageStdProps> = (props) => (
  <Suspense fallback={null}>
    <RegisterPageStd {...props} />
  </Suspense>
)

export default RegisterPageStdSuspended
